import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { API_BASE_URL } from '../../config/api';
import { formatDateKST } from '../../utils/dateUtils';

interface PeriodComparisonComponentProps {
  studentId: string;
  comparisonType: 'weekly' | 'monthly';
}

interface DailyData {
  date: string;
  dateLabel: string;
  personal: number; // 개인 공부량 (시간)
  average: number;  // 조직 평균 (시간)
}

const PeriodComparisonComponent: React.FC<PeriodComparisonComponentProps> = ({
  studentId,
}) => {
  const [chartData, setChartData] = useState<DailyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'1week' | '2weeks' | '1month' | '3months' | '6months'>('1month');
  const [summary, setSummary] = useState({
    personalTotal: 0,
    averageTotal: 0,
    personalAvgDaily: 0,
    averageAvgDaily: 0,
  });

  useEffect(() => {
    fetchComparisonData();
  }, [studentId, period]);

  const getPeriodDays = () => {
    switch (period) {
      case '1week': return 7;
      case '2weeks': return 14;
      case '1month': return 30;
      case '3months': return 90;
      case '6months': return 180;
      default: return 30;
    }
  };

  const fetchComparisonData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const headers = { Authorization: `Bearer ${token}` };

      const days = getPeriodDays();
      const endDate = new Date();
      const startDate = new Date(Date.now() - (days - 1) * 24 * 60 * 60 * 1000);

      const startStr = formatDateKST(startDate);
      const endStr = formatDateKST(endDate);

      // 1. 개인 학습 기록 조회 (daily_study_time 테이블)
      const personalResponse = await fetch(
        `${API_BASE_URL}/api/v1/owner/study-time/student/${studentId}/history?start_date=${startStr}&end_date=${endStr}`,
        { headers }
      );

      let personalRecords: any[] = [];
      if (personalResponse.ok) {
        const data = await personalResponse.json();
        personalRecords = data.records || [];
      }

      // 2. 조직 평균 조회 (전체 학생 일별 평균)
      const avgResponse = await fetch(
        `${API_BASE_URL}/api/v1/owner/study-time/organization/daily-average?start_date=${startStr}&end_date=${endStr}`,
        { headers }
      );

      let avgRecords: any[] = [];
      if (avgResponse.ok) {
        const data = await avgResponse.json();
        avgRecords = data.daily_averages || [];
      }

      // 3. 날짜별 데이터 병합
      const dateMap = new Map<string, DailyData>();

      // 날짜 범위 생성
      for (let i = 0; i < days; i++) {
        const d = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
        const dateStr = formatDateKST(d);
        const dateLabel = `${d.getMonth() + 1}/${d.getDate()}`;
        dateMap.set(dateStr, {
          date: dateStr,
          dateLabel,
          personal: 0,
          average: 0,
        });
      }

      // 개인 데이터 채우기
      personalRecords.forEach((record: any) => {
        const date = record.date;
        if (dateMap.has(date)) {
          const item = dateMap.get(date)!;
          item.personal = Number((record.total_study_time / 60).toFixed(1)); // 분 → 시간
        }
      });

      // 평균 데이터 채우기
      avgRecords.forEach((record: any) => {
        const date = record.date;
        if (dateMap.has(date)) {
          const item = dateMap.get(date)!;
          item.average = Number((record.average_minutes / 60).toFixed(1)); // 분 → 시간
        }
      });

      const finalData = Array.from(dateMap.values());
      setChartData(finalData);

      // 요약 계산
      const personalTotal = finalData.reduce((sum, d) => sum + d.personal, 0);
      const averageTotal = finalData.reduce((sum, d) => sum + d.average, 0);
      const activeDays = finalData.filter(d => d.personal > 0).length;

      setSummary({
        personalTotal: Number(personalTotal.toFixed(1)),
        averageTotal: Number(averageTotal.toFixed(1)),
        personalAvgDaily: activeDays > 0 ? Number((personalTotal / activeDays).toFixed(1)) : 0,
        averageAvgDaily: Number((averageTotal / days).toFixed(1)),
      });

    } catch (error) {
      console.error('Failed to fetch comparison data:', error);
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">데이터 로딩 중...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 기간 선택 */}
      <div className="flex gap-2">
        <button
          onClick={() => setPeriod('1week')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            period === '1week'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          1주일
        </button>
        <button
          onClick={() => setPeriod('2weeks')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            period === '2weeks'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          2주일
        </button>
        <button
          onClick={() => setPeriod('1month')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            period === '1month'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          1개월
        </button>
        <button
          onClick={() => setPeriod('3months')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            period === '3months'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          3개월
        </button>
        <button
          onClick={() => setPeriod('6months')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            period === '6months'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          6개월
        </button>
      </div>

      {/* 꺾은선 그래프 */}
      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="dateLabel"
              tick={{ fontSize: 11 }}
              interval={period === '6months' ? 6 : period === '3months' ? 4 : period === '1month' ? 2 : 0}
            />
            <YAxis
              label={{ value: '시간', angle: -90, position: 'insideLeft', fontSize: 12 }}
              tickFormatter={(value) => `${value}h`}
            />
            <Tooltip
              formatter={(value: any, name?: string) => {
                const label = name === 'personal' ? '내 공부량' : '전체 평균';
                return [`${value}시간`, label];
              }}
              labelFormatter={(label) => `${label}`}
            />
            <Legend
              formatter={(value) => value === 'personal' ? '내 공부량' : '전체 평균'}
            />
            <Line
              type="monotone"
              dataKey="personal"
              stroke="#3B82F6"
              strokeWidth={2}
              dot={{ fill: '#3B82F6', r: 3 }}
              activeDot={{ r: 5 }}
            />
            <Line
              type="monotone"
              dataKey="average"
              stroke="#10B981"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ fill: '#10B981', r: 3 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-[300px] flex items-center justify-center text-gray-500">
          데이터가 없습니다.
        </div>
      )}

      {/* 요약 카드 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4 text-center">
          <p className="text-sm text-gray-600 mb-1">내 총 공부량</p>
          <p className="text-2xl font-bold text-blue-600">{summary.personalTotal}h</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4 text-center">
          <p className="text-sm text-gray-600 mb-1">전체 평균</p>
          <p className="text-2xl font-bold text-green-600">{summary.averageTotal}h</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-4 text-center">
          <p className="text-sm text-gray-600 mb-1">내 일평균</p>
          <p className="text-2xl font-bold text-blue-600">{summary.personalAvgDaily}h</p>
          <p className="text-xs text-gray-400">출석일 기준</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4 text-center">
          <p className="text-sm text-gray-600 mb-1">전체 일평균</p>
          <p className="text-2xl font-bold text-green-600">{summary.averageAvgDaily}h</p>
        </div>
      </div>

      {/* 비교 분석 */}
      {summary.personalTotal > 0 && summary.averageTotal > 0 && (
        <div className={`p-4 rounded-lg ${
          summary.personalTotal >= summary.averageTotal ? 'bg-blue-50' : 'bg-yellow-50'
        }`}>
          <p className={`text-sm font-medium ${
            summary.personalTotal >= summary.averageTotal ? 'text-blue-700' : 'text-yellow-700'
          }`}>
            {summary.personalTotal >= summary.averageTotal
              ? `✨ 전체 평균보다 ${(summary.personalTotal - summary.averageTotal).toFixed(1)}시간 더 공부했습니다!`
              : `💪 전체 평균까지 ${(summary.averageTotal - summary.personalTotal).toFixed(1)}시간 더 필요합니다.`
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default PeriodComparisonComponent;
